import { Badge, Button, Header, Icon, Text, Tooltip } from '@opengov/component-library';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-markup';
import React, { useEffect, useMemo, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import { IPublicationCustomCssMessages } from '../../../../../types';
import { CodeEditor } from '../../../../common/components/CodeEditor';
import {
  getElementAttributes,
  getElementFullStylesheet,
  getElementPossibleSelectors,
  getValidClassNames
} from '../../../../utils/stylesheetsUtils';

import styles from './ElementInspector.scss';

export interface IElementInspectorProps {
  activeElement: HTMLElement | null;
  iframeDocumentRef: React.MutableRefObject<Document | null>;
  messages: IPublicationCustomCssMessages;
  onCopySelector(selector: string): void;
  setActiveElement(activeElement: HTMLElement | null): void;
}

export interface IActiveElementProps {
  tagName: string | null;
  parentElementInfo: {
    element: HTMLElement;
    badge: string;
    html: string;
  } | null;
  selectors: string[];
  stylesheet: string;
}

const getParentElementBadge = (parentElement: HTMLElement) => {
  if (!parentElement) return null;
  let badge = parentElement.tagName;
  if (parentElement.classList.length) {
    badge += getValidClassNames(parentElement).reduce((acc, className) => `${acc}.${className}`, '');
  }
  return badge;
};

const getParentElementHTML = (parentElement: HTMLElement) => {
  const tagName = parentElement.tagName.toLowerCase();
  let childrenHTML = '';
  Array.from(parentElement.children).forEach((child) => {
    childrenHTML += `\n  <${child.tagName.toLowerCase()}${getElementAttributes(child)}/>`;
  });
  return `<${tagName}${getElementAttributes(parentElement)}>${childrenHTML}\n</${tagName}>`;
};

export const ElementInspector = (props: IElementInspectorProps) => {
  const { activeElement, messages, iframeDocumentRef, onCopySelector, setActiveElement } = props;
  const [isActiveElementSectionDisplayed, setIsActiveElementSectionDisplayed] = useState<boolean>(false);

  useEffect(() => {
    if (!!activeElement) {
      setIsActiveElementSectionDisplayed(true);
    }
  }, [activeElement]);

  const { tagName, selectors, stylesheet, parentElementInfo } = useMemo<IActiveElementProps>(
    () => ({
      tagName: activeElement?.tagName,
      parentElementInfo: !activeElement?.parentElement
        ? null
        : {
            element: activeElement.parentElement,
            badge: getParentElementBadge(activeElement.parentElement),
            html: getParentElementHTML(activeElement.parentElement)
          },
      selectors: getElementPossibleSelectors(activeElement),
      stylesheet: getElementFullStylesheet(activeElement, iframeDocumentRef.current)
    }),
    [activeElement]
  );

  return (
    <div className={styles.container}>
      <div
        className={styles.activeElementHeader}
        data-test="active-element-header"
        role="button"
        onClick={() => {
          setIsActiveElementSectionDisplayed(!isActiveElementSectionDisplayed);
        }}
      >
        <Header variant="h4" roll="heading">
          <Icon
            data-test="active-element-header-icon"
            icon={isActiveElementSectionDisplayed ? 'arrow-chevron-down' : 'arrow-chevron-right'}
            ariaLabel="open active element section"
          />
          {messages.activeElement}
        </Header>
      </div>
      {isActiveElementSectionDisplayed && (
        <div className={styles.content} data-test="active-element-section">
          <div className={styles.infoRow}>
            <Text font="condensed" weight="medium" size="small" color="light">
              {messages.tagName}:
            </Text>
            {!!tagName && <Badge text={tagName} dataTest="activeElementTagName" />}
          </div>
          <div className={styles.infoCol}>
            <Text font="condensed" weight="medium" size="small" color="light">
              {messages.possibleSelectors}:
            </Text>
            {selectors.map((selector) => (
              <div key={selector}>
                <Badge
                  className={styles.selectorBadge}
                  variant="bright"
                  text={selector}
                  dataTest="activeElementSelector"
                />
                <CopyToClipboard text={selector}>
                  <Button
                    variant="naked"
                    size="small"
                    className={styles.iconButton}
                    dataTest="copyActiveElementSelectorBtn"
                    onClick={() => onCopySelector(selector)}
                  >
                    <Icon icon="action-duplicate" />
                  </Button>
                </CopyToClipboard>
              </div>
            ))}
          </div>

          <div className={styles.infoCol}>
            <Text font="condensed" weight="medium" size="small" color="light">
              {messages.styles}:
            </Text>
            <CodeEditor
              wrapperClassName={styles.cssCodeBlock}
              contentClassName={styles.codeBlockContent}
              dataTest="activeElementStyles"
              disabled={true}
              value={stylesheet}
              onValueChange={() => {}}
              padding={8}
              highlight={(code) => highlight(code, languages.css)}
              style={{ fontFamily: 'monospace', fontSize: 14, lineHeight: 1.1 }}
            />
          </div>

          {!!parentElementInfo && (
            <>
              <div className={styles.infoRow} data-test="parentElementInfo">
                <Text font="condensed" weight="medium" size="small" color="light">
                  {messages.parentElement}:
                </Text>
                <Badge
                  dataTest="parentElementBadge"
                  variant="bright"
                  className={styles.selectorBadge}
                  text={parentElementInfo.badge}
                />
                <Tooltip content={messages.makeElementActive}>
                  <Button
                    variant="naked"
                    size="small"
                    className={styles.iconButton}
                    onClick={() => setActiveElement(parentElementInfo.element)}
                    dataTest="makeParentActiveBtn"
                  >
                    <Icon icon="action-enter" />
                  </Button>
                </Tooltip>
              </div>
              <CodeEditor
                wrapperClassName={styles.markupCodeBlock}
                contentClassName={styles.codeBlockContent}
                dataTest="parentElementHtml"
                disabled={true}
                value={parentElementInfo.html}
                onValueChange={() => {}}
                padding={8}
                highlight={(code) => highlight(code, languages.markup)}
                style={{ fontFamily: 'monospace', fontSize: 14, lineHeight: 1.1 }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};
